/**
 * AtribuirTecnicoDialog — modal admin pra atribuir técnico + data/hora a
 * um chamado da Corretiva (BUG-B1.6.2 Phase 2).
 *
 * Fluxo:
 *   1. Admin clica num card "Aberta" no Kanban Corretiva
 *   2. ChamadoDealDetailDialog abre, mostra detalhes + botão "Atribuir técnico"
 *   3. Click no botão abre este dialog
 *   4. Form: técnico (dropdown filtrado dept Manutenção) + data/hora (<Calendario> canônico) + obs
 *   5. Submit → POST /api/v1/admin/chamados/:dealId/atribuir-tecnico
 *      Backend cria OS linkada + move deal pra Agendadas + notifica
 *   6. Dialog fecha, Kanban refetch automático (invalidate)
 *
 * NÃO REVERTER:
 *   - data+hora vêm do <Calendario> canônico (FILA-05AGO); o valor continua
 *     no formato 'YYYY-MM-DDTHH:mm' que o submit converte pra ISO
 *   - Filtra técnicos por user_department_roles.department_slug='manutencao'
 *     (mesmo critério dos tecnicosDisponiveis em ManutencaoPage)
 *   - Validação client-side mínima (datetime no futuro) — backend valida real
 *   - Submit invalida queryKey ['v1','deals','list','corretiva',...] +
 *     ['v1','service-orders',...] pra Kanban + Modo Técnico refresh
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Alert,
  Button,
  Calendario,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectOption,
  useToast,
} from '@/components/ui';
import { useUsers } from '@/lib/admin-store';
import { useActivityTypes } from '@/lib/activity-types-store';
import { api } from '@/lib/api';

interface AtribuirTecnicoDialogProps {
  open: boolean;
  dealId: string;
  dealTitulo: string;
  dealNumeroHumano: number | null;
  onClose: () => void;
  /** Callback após sucesso — caller refresh sua view. */
  onAssigned?: () => void;
}

interface AtribuirResponse {
  osId: string;
  osNumeroHumano: number;
  osTokenPublico: string;
  osPublicUrl: string;
  notificouCliente: boolean;
  notificouTecnico: boolean;
  faseDeal: 'agendada';
}

export function AtribuirTecnicoDialog({
  open,
  dealId,
  dealTitulo,
  dealNumeroHumano,
  onClose,
  onAssigned,
}: AtribuirTecnicoDialogProps) {
  const users = useUsers();
  const qc = useQueryClient();
  const toast = useToast();

  const [tecnicoId, setTecnicoId] = useState('');
  const [tipoAtendimentoId, setTipoAtendimentoId] = useState('');
  const [agendadoLocal, setAgendadoLocal] = useState(''); // datetime-local string
  const [observacao, setObservacao] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // BUG-B1.6.2 (FEAT-B1.6.4): tipos de atendimento vindos de activity_types
  // categoria='task' (admin gerencia em /admin/tipos-atividade). Filtra por
  // workflow_slug='corretiva' OU global (workflow_slug=NULL).
  const { data: tiposAtendimento = [] } = useActivityTypes({ categoria: 'task' });
  const tiposCorretiva = useMemo(
    () =>
      tiposAtendimento
        .filter((t) => t.ativo && (!t.workflowSlug || t.workflowSlug === 'corretiva'))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
    [tiposAtendimento],
  );

  // Técnicos disponíveis = users ativos com vínculo no dept Manutenção
  const tecnicosDisponiveis = useMemo(
    () =>
      users
        .filter(
          (u) =>
            u.ativo && u.departamentos.some((dr) => dr.departmentSlug === 'manutencao'),
        )
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [users],
  );

  const atribuir = useMutation({
    mutationFn: async () => {
      // datetime-local vem como "2026-05-29T14:00" sem timezone
      // Convertemos pra ISO assumindo timezone local (browser)
      const agendadoIso = agendadoLocal ? new Date(agendadoLocal).toISOString() : '';
      // Resolve label do tipo escolhido (vai como `tipo` da OS)
      const tipoLabel = tiposCorretiva.find((t) => t.id === tipoAtendimentoId)?.label ?? null;
      const r = await api.post<AtribuirResponse>(
        `/api/v1/admin/chamados/${encodeURIComponent(dealId)}/atribuir-tecnico`,
        {
          tecnicoId,
          agendadoParaIso: agendadoIso,
          observacao: observacao.trim() || null,
          tipoAtendimentoId: tipoAtendimentoId || null,
          tipoAtendimentoLabel: tipoLabel,
        },
      );
      return r;
    },
    onSuccess: (r) => {
      // Invalida lists de deals + service-orders pro Kanban e o /tecnico
      qc.invalidateQueries({ queryKey: ['v1', 'deals'] });
      qc.invalidateQueries({ queryKey: ['v1', 'service-orders'] });
      qc.invalidateQueries({ queryKey: ['cliente', 'chamados'] });
      qc.invalidateQueries({ queryKey: ['cliente', 'meus-chamados'] });
      qc.invalidateQueries({ queryKey: ['cliente', 'chamado'] });
      toast.show(
        `Técnico atribuído! OS #${r.osNumeroHumano} criada.${
          r.notificouCliente ? ' Cliente notificado.' : ''
        }`,
        { variant: 'success' },
      );
      onAssigned?.();
      onClose();
      // Reset form
      setTecnicoId('');
      setAgendadoLocal('');
      setObservacao('');
      setErrorMsg(null);
    },
    onError: (err) => {
      setErrorMsg(`Erro: ${(err as Error).message ?? 'desconhecido'}`);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (!tecnicoId) {
      setErrorMsg('Selecione um técnico.');
      return;
    }
    if (!tipoAtendimentoId) {
      setErrorMsg('Selecione o tipo de atendimento.');
      return;
    }
    if (!agendadoLocal) {
      setErrorMsg('Selecione data e hora do atendimento.');
      return;
    }
    // Sanity check: data não pode ser passado
    const dt = new Date(agendadoLocal);
    if (Number.isNaN(dt.getTime())) {
      setErrorMsg('Data/hora inválida.');
      return;
    }
    if (dt.getTime() < Date.now() - 60_000) {
      // 1min de tolerância
      setErrorMsg('Data/hora não pode ser no passado.');
      return;
    }
    atribuir.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>
          Atribuir técnico ·{' '}
          <span className="text-ink-3 dark:text-ink-dark-3">
            {dealNumeroHumano != null ? `#${dealNumeroHumano} ` : ''}
            {dealTitulo}
          </span>
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <form
          onSubmit={handleSubmit}
          className="space-y-4"
          data-testid="atribuir-tecnico-form"
        >
          <div>
            <Label htmlFor="at-tecnico">Técnico*</Label>
            <Select
              id="at-tecnico"
              value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
              disabled={atribuir.isPending}
              data-testid="atribuir-tecnico-select"
            >
              <SelectOption value="">Selecione…</SelectOption>
              {tecnicosDisponiveis.map((t) => (
                <SelectOption key={t.id} value={t.id}>
                  {t.nome}
                </SelectOption>
              ))}
            </Select>
            {tecnicosDisponiveis.length === 0 && (
              <p className="text-xs text-warning mt-1">
                Nenhum técnico cadastrado no departamento Manutenção. Cadastre em
                Configurações → Pessoas e acesso → Usuários.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="at-tipo">Tipo de atendimento*</Label>
            <Select
              id="at-tipo"
              value={tipoAtendimentoId}
              onChange={(e) => setTipoAtendimentoId(e.target.value)}
              disabled={atribuir.isPending}
              data-testid="atribuir-tecnico-tipo"
            >
              <SelectOption value="">Selecione…</SelectOption>
              {tiposCorretiva.map((t) => (
                <SelectOption key={t.id} value={t.id}>
                  {t.label}
                </SelectOption>
              ))}
            </Select>
            {tiposCorretiva.length === 0 && (
              <p className="text-xs text-warning mt-1">
                Nenhum tipo de atendimento cadastrado. Cadastre em
                Configurações → Catálogos → Tipos de atividade (categoria: Tarefa).
              </p>
            )}
          </div>

          <div>
            <Label>Data e hora do atendimento*</Label>
            <Calendario
              valor={agendadoLocal}
              onChange={setAgendadoLocal}
              desabilitado={atribuir.isPending}
              placeholder="Escolher data e hora"
              testId="atribuir-tecnico-datahora"
            />
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
              Cliente vai receber a data + nome do técnico por email (se o
              toggle &quot;Notificar agendamento&quot; estiver ligado no cadastro).
            </p>
          </div>

          <div>
            <Label htmlFor="at-obs">Observação (opcional)</Label>
            <textarea
              id="at-obs"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              placeholder="Ex: levar chave de fenda 6mm..."
              rows={3}
              maxLength={2000}
              disabled={atribuir.isPending}
              className="block w-full px-3 py-2 rounded border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-elevated focus:outline-none focus:ring-2 focus:ring-red"
              data-testid="atribuir-tecnico-obs"
            />
          </div>

          {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
        </form>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={atribuir.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={atribuir.isPending || tecnicosDisponiveis.length === 0}
          data-testid="atribuir-tecnico-submit"
        >
          {atribuir.isPending ? 'Atribuindo…' : 'Atribuir + Agendar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
